import React, { useState, useRef, useEffect } from 'react';
import {
  TextField,
  Label,
  TextArea as AriaTextArea,
} from 'react-aria-components';
import clsx from 'clsx';
import styles from './CustomTextArea.module.css';

export interface CustomTextAreaProps {
  /**
   * Textarea label
   */
  label?: string;
  /**
   * Error message to display
   */
  error?: string;
  /**
   * Helper text to display below the textarea
   */
  helperText?: string;
  /**
   * Full width textarea
   */
  fullWidth?: boolean;
  /**
   * Textarea value
   */
  value?: string;
  /**
   * Change handler
   */
  onChange?: (value: string) => void;
  /**
   * Placeholder text
   */
  placeholder?: string;
  /**
   * Additional CSS class
   */
  className?: string;
  /**
   * Disabled state
   */
  disabled?: boolean;
  /**
   * Required field
   */
  required?: boolean;
  /**
   * Enable character counter
   */
  showCharCount?: boolean;
  /**
   * Maximum character length
   */
  maxLength?: number;
  /**
   * Enable clear button
   */
  clearable?: boolean;
  /**
   * Callback when textarea is cleared
   */
  onClear?: () => void;
  /**
   * Callback on blur
   */
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  /**
   * Callback on focus
   */
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  /**
   * Callback on key down
   */
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  /**
   * Name attribute for the textarea
   */
  name?: string;
  /**
   * ID attribute for the textarea
   */
  id?: string;
  /**
   * Auto focus on mount
   */
  autoFocus?: boolean;
  /**
   * Read only state
   */
  readOnly?: boolean;
  /**
   * Number of rows
   */
  rows?: number;
  /**
   * Resize behavior
   */
  resize?: 'none' | 'vertical' | 'horizontal' | 'both';
  /**
   * Custom border color (only applies to default state, not focus/error)
   */
  borderColor?: string;
  /**
   * Remove border radius
   */
  hasNoRadius?: boolean;
  /**
   * Remove box shadow
   */
  hasNoBoxShadow?: boolean;
}

/**
 * CustomTextArea component - an enhanced textarea with clear button and character count
 */
export const CustomTextArea: React.FC<CustomTextAreaProps> = ({
  label,
  error,
  helperText,
  fullWidth = false,
  value = '',
  onChange,
  placeholder,
  className = '',
  disabled = false,
  required = false,
  showCharCount = false,
  maxLength,
  clearable = false,
  onClear,
  onBlur,
  onFocus,
  onKeyDown,
  name,
  id,
  autoFocus = false,
  readOnly = false,
  rows = 4,
  resize = 'vertical',
  borderColor,
  hasNoRadius = false,
  hasNoBoxShadow = false,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [currentValue, setCurrentValue] = useState(value || '');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Sync internal state with prop changes
  useEffect(() => {
    setCurrentValue(value || '');
  }, [value]);

  const hasError = !!error;
  const charCount = (currentValue || '').length;
  const showClearButton = clearable && currentValue && currentValue.length > 0 && !disabled && !readOnly;

  const handleChange = (newValue: string) => {
    setCurrentValue(newValue);
    onChange?.(newValue);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentValue('');
    onChange?.('');
    onClear?.();
    textareaRef.current?.focus();
  };

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  // Sync external value changes
  React.useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  return (
    <TextField
      className={clsx(
        styles.wrapper,
        { [styles.wrapperFullWidth]: fullWidth },
        className
      )}
      style={{
        ...(borderColor ? { '--custom-border-color': borderColor } as React.CSSProperties : {}),
      }}
      isDisabled={disabled}
      isRequired={required}
      isReadOnly={readOnly}
      value={currentValue}
      onChange={handleChange}
      name={name}
      id={id}
    >
      {label && (
        <Label className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </Label>
      )}
      
      <div
        className={clsx(
          styles.textareaWrapper,
          {
            [styles.textareaWrapperFocused]: isFocused,
            [styles.textareaWrapperError]: hasError,
            [styles.textareaWrapperDisabled]: disabled,
            [styles.textareaWrapperCustomBorder]: !!borderColor,
            [styles.textareaWrapperNoRadius]: hasNoRadius,
            [styles.textareaWrapperNoBoxShadow]: hasNoBoxShadow,
          }
        )}
      >
        <AriaTextArea
          ref={textareaRef}
          placeholder={placeholder}
          className={clsx(
            styles.textarea,
            styles[`textareaResize${resize.charAt(0).toUpperCase() + resize.slice(1)}`],
            {
              [styles.textareaDisabled]: disabled,
              [styles.textareaReadOnly]: readOnly,
            }
          )}
          style={{
            minHeight: `${rows * 24}px`,
          }}
          maxLength={maxLength}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={onKeyDown}
          autoFocus={autoFocus}
          rows={rows}
          aria-invalid={hasError}
          aria-describedby={error || helperText ? 'textarea-helper' : undefined}
        />
        
        {showClearButton && (
          <button
            type="button"
            onClick={handleClear}
            className={styles.clearButton}
            aria-label="Clear textarea"
          >
            ×
          </button>
        )}
      </div>

      {((error || helperText) || showCharCount) && (
        <div
          id="textarea-helper"
          className={clsx(styles.helper, { [styles.helperError]: hasError })}
        >
          <span>{error || helperText}</span>
          {showCharCount && (
            <span className={clsx(styles.charCount, { [styles.charCountError]: maxLength && charCount > maxLength })}>
              {charCount}{maxLength ? `/${maxLength}` : ''}
            </span>
          )}
        </div>
      )}
    </TextField>
  );
};

CustomTextArea.displayName = 'ProdiktCustomTextArea';
